import { useState, useMemo } from "react";
import {
  ArrowLeft,
  Sparkles,
  Zap,
  Flame,
  Award,
  Footprints,
  Star,
  Moon,
  HeartPulse,
  ChefHat,
  Loader2,
  ChevronDown,
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import BottomNavBar from "../../components/BottomNavBar";
import welcomeBg from "../../assets/homebg.png";
import { HomePageData } from "../../services/HomeService";
import aiInsightsService from "../../services/AIInsightsService";
import { cn } from "../../lib/utils";

// ─── Types ───────────────────────────────────────────────────────────────────

type BadgeType = "Action Needed" | "Great Work" | "Suggestion" | "Good Job";
type TabType = "Insights" | "Recipes";

interface InsightCard {
  icon: React.ReactNode;
  iconBg: string;
  badge: BadgeType;
  badgeBg: string;
  source: string;
  title: string;
  description: string;
  stat?: string;
  statLabel?: string;
  statColor: string;
  cardBg: string;
  borderColor: string;
}

interface RecipeCard {
  emoji: string;
  iconBg: string;
  name: string;
  time: string;
  kcal: number;
  predictedSpike: number;
  protein: number;
  carbs: number;
  netCarbs: number;
  fat: number;
  fiber: number;
  spikeLabel: "Low" | "Moderate" | "High";
  bbsBoost: number;
  tip: string;
  swap?: string;
}

// ─── Insight builder ─────────────────────────────────────────────────────────

function buildInsights(homeData?: HomePageData): InsightCard[] {
  // Extract real API data
  const meals = homeData?.["Glucose Spikes"]?.meals ?? [];
  const dinnerSpike = meals.find((m) => m.meal_type?.toLowerCase() === "dinner")?.this_meal_spike_mg_dl;
  const avgSpike = homeData?.["Glucose Spikes"]?.daily_summary?.avg_this_meal_spike_mg_dl;
  const bbs = homeData?.["Bite Balance Score"]?.daily_bbs;
  const sleep = homeData?.["Today's Activity"]?.Sleep;
  const sleepHours = typeof sleep === "number" ? sleep : (typeof sleep === "string" ? parseFloat(sleep) : null);

  // Glucose stat: use real dinner spike or avg, fallback to example
  const glucoseStat =
    dinnerSpike != null
      ? `+${dinnerSpike.toFixed(1)} mg/dL`
      : avgSpike != null
      ? `+${avgSpike.toFixed(1)} mg/dL`
      : "+52.8 mg/dL";

  // BBS stat: use real value, fallback to example
  const bbsStat = bbs != null ? `${Math.round(bbs)} / 100` : "74 / 100";
  const bbsStatColor = bbs != null && bbs >= 60 ? "text-teal-600" : "text-amber-600";

  // Sleep-based badge: if sleep < 7h → Action Needed, else Suggestion
  const sleepBadge: BadgeType = sleepHours != null && sleepHours < 7 ? "Action Needed" : "Suggestion";

  const cards: InsightCard[] = [
    // 1. Glucose
    {
      icon: <Flame className="h-5 w-5" />,
      iconBg: "bg-orange-100 text-orange-500",
      badge: "Action Needed",
      badgeBg: "bg-red-100 text-red-600",
      source: "Glucose",
      title: "Dinner spikes are 2x higher than breakfast",
      description: "Add fiber-rich sides like broccoli or lentils to evening meals to blunt the spike.",
      stat: glucoseStat,
      statLabel: "Dinner avg",
      statColor: "text-red-500",
      cardBg: "bg-red-50/70",
      borderColor: "border-red-200/60",
    },
    // 2. BBS
    {
      icon: <Award className="h-5 w-5" />,
      iconBg: "bg-teal-100 text-teal-600",
      badge: "Great Work",
      badgeBg: "bg-emerald-100 text-emerald-700",
      source: "BBS",
      title: "BBS improved 4 points this week",
      description: "Better lunch choices are paying off — keep choosing high-protein, fiber-rich meals.",
      stat: bbsStat,
      statLabel: "Current BBS",
      statColor: bbsStatColor,
      cardBg: "bg-teal-50/70",
      borderColor: "border-teal-200/60",
    },
    // 3. Activity
    {
      icon: <Footprints className="h-5 w-5" />,
      iconBg: "bg-purple-100 text-purple-500",
      badge: "Suggestion",
      badgeBg: "bg-yellow-100 text-yellow-700",
      source: "Activity",
      title: "15-min post-dinner walk could cut spikes 25%",
      description: "Your Fitbit shows sedentary evenings. A short walk after dinner reduces glucose spikes.",
      stat: "-25%",
      statLabel: "Potential cut",
      statColor: "text-pink-500",
      cardBg: "bg-yellow-50/60",
      borderColor: "border-yellow-200/60",
    },
    // 4. Sleep
    {
      icon: <Moon className="h-5 w-5" />,
      iconBg: "bg-indigo-100 text-indigo-500",
      badge: sleepBadge,
      badgeBg: "bg-yellow-100 text-yellow-700",
      source: "Sleep",
      title: "Poor sleep = higher next-day spikes",
      description: "On days with <7h sleep, spikes are 18% higher. Eat lower-GI today.",
      stat: "+18%",
      statLabel: "Spike increase",
      statColor: "text-indigo-500",
      cardBg: "bg-indigo-50/60",
      borderColor: "border-indigo-200/60",
    },
  ];

  return cards;
}


// ─── Recipe builder ────────────────────────────────────────────────────────

function buildRecipes(homeData?: HomePageData): RecipeCard[] {
  const avgSpike = homeData?.["Glucose Spikes"]?.daily_summary?.avg_this_meal_spike_mg_dl;
  const bbs = homeData?.["Bite Balance Score"]?.daily_bbs;

  const all: RecipeCard[] = [
    {
      emoji: "🥗",
      iconBg: "bg-emerald-50",
      name: "Bean and Avocado Salad",
      time: "30 min",
      kcal: 380,
      predictedSpike: 28,
      protein: 14,
      carbs: 32,
      netCarbs: 20,
      fat: 18,
      fiber: 12,
      spikeLabel: "Low",
      bbsBoost: 5,
      tip: "This salad provides a fiber-rich meal to address Aniya's fiber intake goal and improve overall balance.",
      swap: "Swap: Use chickpeas instead of beans for a creamier texture and extra protein boost.",
    },
    {
      emoji: "🥘",
      iconBg: "bg-amber-50",
      name: "Vegetable Stir-Fry with Tofu",
      time: "15 min + 15 min cook",
      kcal: 310,
      predictedSpike: 30,
      protein: 22,
      carbs: 28,
      netCarbs: 18,
      fat: 12,
      fiber: 10,
      spikeLabel: "Moderate",
      bbsBoost: 4,
      tip: "A good source of plant-based protein to improve Arjun's protein intake while keeping glycemic load low.",
      swap: "Swap: Use tempeh instead of tofu for a variety of taste and texture.",
    },
    {
      emoji: "🥣",
      iconBg: "bg-orange-50",
      name: "Overnight Oats with Chia Seeds",
      time: "10 min",
      kcal: 290,
      predictedSpike: 26,
      protein: 11,
      carbs: 32,
      netCarbs: 23,
      fat: 9,
      fiber: 9,
      spikeLabel: "Low",
      bbsBoost: 6,
      tip: "A fiber-rich breakfast option to start your day while enhancing Ayun's low fiber consumption.",
      swap: "Swap: Use almond milk instead of regular milk for a lower-calorie, dairy-free alternative.",
    },
  ];

  // Prioritise high-spike recipes when avg spike is elevated
  if (avgSpike != null && avgSpike > 30) {
    return all.sort((a, b) => a.predictedSpike - b.predictedSpike).slice(0, 3);
  }
  // Prioritise high BBS-boost when BBS is low
  if (bbs != null && bbs < 50) {
    return all.sort((a, b) => b.bbsBoost - a.bbsBoost).slice(0, 3);
  }
  return all.slice(0, 3);
}

// ─── Badge color helper ────────────────────────────────────────────────────

const badgeColors: Record<BadgeType, string> = {
  "Action Needed": "bg-red-100 text-red-600",
  "Great Work": "bg-emerald-100 text-emerald-700",
  Suggestion: "bg-yellow-100 text-yellow-700",
  "Good Job": "bg-blue-100 text-blue-700",
};

// ─── Sentiment helpers (live API) ─────────────────────────────────────────

const sentimentLabel: Record<string, string> = {
  action_needed: "Action Needed",
  great_work: "Great Work",
  suggestion: "Suggestion",
  insight: "Insight",
};

const sentimentStyles: Record<string, { badge: string; cardBg: string; border: string; icon: string }> = {
  action_needed: { badge: "bg-red-100 text-red-600",     cardBg: "bg-red-50/70",     border: "border-red-200/60",     icon: "bg-orange-100 text-orange-500" },
  great_work:    { badge: "bg-emerald-100 text-emerald-700", cardBg: "bg-teal-50/70",  border: "border-teal-200/60",  icon: "bg-teal-100 text-teal-600" },
  suggestion:    { badge: "bg-yellow-100 text-yellow-700",cardBg: "bg-yellow-50/60",  border: "border-yellow-200/60",icon: "bg-purple-100 text-purple-500" },
  insight:       { badge: "bg-blue-100 text-blue-700",   cardBg: "bg-blue-50/60",    border: "border-blue-200/60",  icon: "bg-blue-100 text-blue-500" },
};

// ─── Icon mapper ───────────────────────────────────────────────────────────

const getIconByType = (iconType: string) => {
  const iconMap: Record<string, React.ReactNode> = {
    flame:      <Flame className="h-5 w-5" />,
    award:      <Award className="h-5 w-5" />,
    footprints: <Footprints className="h-5 w-5" />,
    walk:       <Footprints className="h-5 w-5" />,
    moon:       <Moon className="h-5 w-5" />,
    sleep:      <Moon className="h-5 w-5" />,
    zap:        <Zap className="h-5 w-5" />,
    spike:      <Zap className="h-5 w-5" />,
    trend:      <Zap className="h-5 w-5" />,
    heart:      <HeartPulse className="h-5 w-5" />,
    nutrition:  <Flame className="h-5 w-5" />,
    sparkles:   <Sparkles className="h-5 w-5" />,
    star:       <Star className="h-5 w-5" />,
  };
  return iconMap[iconType.toLowerCase()] ?? <Sparkles className="h-5 w-5" />;
};

/** Recipe accordion — consistent section titles vs body copy */
const RecipeSectionHeading = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <h3
    className={cn(
      "mb-2 border-b border-border/60 pb-1.5 text-caption2 font-bold uppercase tracking-[0.08em] text-foreground",
      className,
    )}
  >
    {children}
  </h3>
);

// ─── Page ────────────────────────────────────────────────────────────────────

const DailyInsightsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Get date from URL param, location state, or default to today
  const getLocalDateString = (date: Date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const selectedDate =
    searchParams.get("date") ||
    location.state?.selectedDate ||
    getLocalDateString();

  const [activeTab, setActiveTab] = useState<TabType>("Insights");
  const [expandedRecipe, setExpandedRecipe] = useState<number | null>(null);

  // Fetch AI Insights from API using React Query
  const {
    data: apiData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["aiInsights", selectedDate],
    queryFn: () =>
      aiInsightsService.getAIInsights(selectedDate, {
        showNotification: false,
      }),
    staleTime: 0,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });

  // Fallback to static data if API fails (backward compatibility)
  const homeData: HomePageData | undefined = location.state?.homeData;
  const staticInsights = useMemo(() => buildInsights(homeData), [homeData]);
  const staticRecipes = useMemo(() => buildRecipes(homeData), [homeData]);

  // Fetch Recipes from live API — only when Recipes tab is active
  const {
    data: recipesData,
    isLoading: recipesLoading,
    isFetching: recipesFetching,
  } = useQuery({
    queryKey: ["aiRecipes", selectedDate],
    queryFn: () =>
      aiInsightsService.getRecipes(selectedDate, { showNotification: false }),
    enabled: activeTab === "Recipes",
    staleTime: 0,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });

  // Use live data if available, fallback to static
  const recipes = recipesData?.recipes?.length ? recipesData.recipes : staticRecipes;

  const formattedDate = selectedDate
    ? new Date(selectedDate + "T00:00:00").toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      })
    : "";

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{ minHeight: "100vh", maxHeight: "100vh", overflow: "hidden" }}
    >
      {/* Blurred BG */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{ backgroundImage: `url(${welcomeBg})` }}
      />
      <div className="absolute inset-0 bg-background/80" />

      {/* Fetching indicator — top pulse bar */}
      {isFetching && !isLoading && (
        <div className="absolute left-0 right-0 top-0 z-20 h-0.5 overflow-hidden bg-muted">
          <div className="h-full w-1/2 animate-[shimmer_1s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-primary to-transparent" />
        </div>
      )}

      <div
        className="scrollbar-hide relative z-10 overflow-y-auto pb-24"
        style={{ height: "100vh" }}
      >
        {/* ── Header (same horizontal inset as tab content) ── */}
        <div className="flex items-start gap-3 px-4 pb-2 pt-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="mt-0.5 h-10 w-10 shrink-0 rounded-xl border border-border bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-title2 font-bold leading-tight text-foreground">Daily Insights</h1>
            <p className="mt-0.5 text-caption1 leading-snug text-muted-foreground">
              Personalized for you{formattedDate ? ` · ${formattedDate}` : ""}
            </p>
          </div>
        </div>

        {/* ── AI-powered banner ── */}
        <div className="mx-4 mb-3 mt-1 flex items-start gap-3 rounded-xl bg-blue-50 px-4 py-2.5 dark:bg-blue-950/30">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/50">
            <HeartPulse className="h-4 w-4 text-blue-500" />
          </div>
          <p className="min-w-0 flex-1 text-caption1 font-medium leading-snug text-blue-800 dark:text-blue-300">
            <span className="font-bold">AI-powered</span> — based on your BBS, glucose, meals &amp; Fitbit
          </p>
        </div>

        {/* ── Tabs ── */}
        <div className="mx-4 mb-4 grid grid-cols-2 rounded-xl bg-muted/50 p-1">
          {(["Insights", "Recipes"] as TabType[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex items-center justify-center gap-1.5 rounded-lg py-2 text-footnote font-semibold transition-all ${
                activeTab === tab
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground"
              }`}
            >
              {tab === "Insights" ? (
                <Sparkles className="h-3.5 w-3.5" />
              ) : (
                <ChefHat className="h-3.5 w-3.5" />
              )}
              {tab}
            </button>
          ))}
        </div>

        {/* ── Insights Tab ── */}
        {activeTab === "Insights" && (
          <div className="space-y-3 px-4">
            {/* ── Today's Focus Card ── */}
            {!isFetching && apiData?.today_focus && (
              <div className="overflow-hidden rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50 shadow-sm dark:border-indigo-800/40 dark:from-indigo-950/40 dark:to-blue-950/30">
                <div className="flex items-start gap-3 p-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-100 dark:bg-indigo-900/50">
                    <Sparkles className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-caption2 font-bold uppercase tracking-wider text-indigo-500 dark:text-indigo-400">
                      Today's Focus
                    </p>
                    <h3 className="mt-1 text-headline font-bold leading-snug text-indigo-900 dark:text-indigo-100">
                      {apiData.today_focus.headline}
                    </h3>
                    <p className="mt-1.5 text-footnote leading-relaxed text-indigo-700/80 dark:text-indigo-300/80">
                      {apiData.today_focus.description}
                    </p>
                  </div>
                </div>
              </div>
            )}
            {/* ── Skeleton while fetching ── */}
            {(isFetching || isLoading) ? (
              <>
                {/* Message */}
                <div className="flex flex-col items-center justify-center gap-4 py-6 text-center">
                  <div className="relative flex h-16 w-16 items-center justify-center">
                    <div className="absolute inset-0 animate-ping rounded-full bg-indigo-100 opacity-60" />
                    <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-indigo-100">
                      <Sparkles className="h-8 w-8 text-indigo-600" />
                    </div>
                  </div>
                  <div>
                    <p className="text-callout font-bold text-foreground">
                      {isLoading ? "Your insights are on the way!" : "Updating your insights…"}
                    </p>
                    <p className="mt-1 text-footnote text-muted-foreground leading-relaxed">
                      {isLoading
                        ? "Our AI is analysing your meals, glucose & activity. This may take a few seconds."
                        : "We're refreshing your insights based on your latest meal data. Hang tight!"}
                    </p>
                  </div>
                </div>
                {/* Skeleton cards */}
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="overflow-hidden rounded-2xl border border-border/60 animate-pulse">
                    <div className="bg-muted/60 px-4 pb-3 pt-3.5">
                      <div className="mb-2.5 flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full bg-muted" />
                        <div className="h-4 w-24 rounded-full bg-muted" />
                        <div className="ml-auto h-3 w-14 rounded-full bg-muted" />
                      </div>
                      <div className="h-4 w-3/4 rounded-lg bg-muted" />
                      <div className="mt-1.5 h-4 w-1/2 rounded-lg bg-muted" />
                    </div>
                    <div className="bg-card px-4 pb-4 pt-3 space-y-2">
                      <div className="h-3 w-full rounded bg-muted/70" />
                      <div className="h-3 w-5/6 rounded bg-muted/70" />
                      <div className="h-3 w-4/6 rounded bg-muted/70" />
                      <div className="mt-3 h-8 w-32 rounded-xl bg-muted/50" />
                    </div>
                  </div>
                ))}
              </>
            ) : (
            <>{/* ── Live API cards ── */}
            {apiData?.cards ? (
              apiData.cards.length === 0 ? (
                <Card className="rounded-2xl border border-border bg-card p-8 text-center">
                  <Sparkles className="mx-auto mb-3 h-8 w-8 text-muted-foreground/40" />
                  <p className="text-callout font-semibold text-foreground">No insights yet</p>
                  <p className="mt-1 text-footnote text-muted-foreground">
                    Log your meals today to unlock personalised insights
                  </p>
                </Card>
              ) : (
                apiData.cards.map((card, i) => {
                  const style = sentimentStyles[card.sentiment] ?? sentimentStyles["insight"];
                  const label = sentimentLabel[card.sentiment] ?? card.sentiment;
                  const metricColor = style.badge.split(" ").find((c) => c.startsWith("text-")) ?? "text-primary";
                  return (
                    <div
                      key={card.id || i}
                      className={`overflow-hidden rounded-2xl border ${style.border}`}
                    >
                      {/* Top accent strip */}
                      <div className={`px-4 pb-3 pt-3.5 ${style.cardBg}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${style.icon}`}>
                            {getIconByType(card.icon_type)}
                          </div>
                          <span className={`rounded-full px-2.5 py-0.5 text-caption2 font-bold ${style.badge}`}>
                            {label}
                          </span>
                          <span className="ml-auto text-caption2 font-semibold capitalize text-muted-foreground">
                            {card.category}
                          </span>
                        </div>
                        <h3 className="text-headline font-bold leading-snug text-foreground">
                          {card.title}
                        </h3>
                      </div>

                      {/* Body */}
                      <div className="bg-card px-4 pb-4 pt-3">
                        <p className="text-footnote leading-relaxed text-muted-foreground">
                          {card.description}
                        </p>
                        {card.metric && (
                          <div className={`mt-3 flex items-baseline gap-1.5 rounded-xl px-3 py-2 ${style.cardBg}`}>
                            <span className={`text-title3 font-black ${metricColor}`}>
                              {card.metric.value}
                            </span>
                            <span className="text-caption1 text-muted-foreground">{card.metric.label}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )
            ) : (
              /* ── Static fallback ── */
              staticInsights.length === 0 ? (
                <Card className="rounded-2xl border border-border bg-card p-8 text-center">
                  <Sparkles className="mx-auto mb-3 h-8 w-8 text-muted-foreground/40" />
                  <p className="text-callout font-semibold text-foreground">No insights yet</p>
                  <p className="mt-1 text-footnote text-muted-foreground">
                    Log your meals today to unlock personalised insights
                  </p>
                </Card>
              ) : (
                staticInsights.map((insight, i) => (
                  <div
                    key={i}
                    className={`overflow-hidden rounded-2xl border ${insight.borderColor}`}
                  >
                    {/* Top accent strip */}
                    <div className={`px-4 pb-3 pt-3.5 ${insight.cardBg}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${insight.iconBg}`}>
                          {insight.icon}
                        </div>
                        <span className={`rounded-full px-2.5 py-0.5 text-caption2 font-bold ${badgeColors[insight.badge]}`}>
                          {insight.badge}
                        </span>
                        <span className="ml-auto text-caption2 font-semibold text-muted-foreground">
                          {insight.source}
                        </span>
                      </div>
                      <h3 className="text-headline font-bold leading-snug text-foreground">
                        {insight.title}
                      </h3>
                    </div>

                    {/* Body */}
                    <div className="bg-card px-4 pb-4 pt-3">
                      <p className="text-footnote leading-relaxed text-muted-foreground">
                        {insight.description}
                      </p>
                      {insight.stat && (
                        <div className={`mt-3 flex items-baseline gap-1.5 rounded-xl px-3 py-2 ${insight.cardBg}`}>
                          <span className={`text-title3 font-black ${insight.statColor}`}>
                            {insight.stat}
                          </span>
                          <span className="text-caption1 text-muted-foreground">{insight.statLabel}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )
            )}
            </>
            )}
          </div>
        )}

        {/* ── Recipes Tab ── */}
        {activeTab === "Recipes" && (
          <div className="space-y-3 px-4">
            {/* Recipe accordion cards */}
            {recipesLoading || recipesFetching ? (
              <div className="flex flex-col items-center justify-center gap-4 py-10 text-center px-4">
                {/* Animated icon */}
                <div className="relative flex h-16 w-16 items-center justify-center">
                  <div className="absolute inset-0 animate-ping rounded-full bg-emerald-100 opacity-60" />
                  <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                    <ChefHat className="h-8 w-8 text-emerald-600" />
                  </div>
                </div>
                {/* Message */}
                <div>
                  <p className="text-callout font-bold text-foreground">
                    {recipesFetching && !recipesLoading
                      ? "Updating your recipes…"
                      : "Your recipes are on the way!"}
                  </p>
                  <p className="mt-1 text-footnote text-muted-foreground leading-relaxed">
                    {recipesFetching && !recipesLoading
                      ? "We're refreshing your plan based on your latest profile updates. Hang tight!"
                      : "Our AI is crafting personalised recipes just for you. This may take a few seconds."}
                  </p>
                </div>
                {/* Skeleton preview cards */}
                <div className="w-full space-y-2 mt-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="overflow-hidden rounded-2xl border border-border/60 animate-pulse">
                      <div className="bg-muted/60 px-4 pb-3 pt-3">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="h-5 w-20 rounded-full bg-muted" />
                          <div className="h-5 w-24 rounded-full bg-muted" />
                        </div>
                        <div className="h-4 w-3/4 rounded-lg bg-muted" />
                      </div>
                      <div className="bg-card px-4 pb-3 pt-2.5 space-y-2">
                        <div className="flex gap-4">
                          <div className="h-3 w-16 rounded bg-muted/70" />
                          <div className="h-3 w-16 rounded bg-muted/70" />
                          <div className="h-3 w-14 rounded bg-muted/70" />
                        </div>
                        <div className="flex gap-4">
                          <div className="h-3 w-14 rounded bg-muted/70" />
                          <div className="h-3 w-16 rounded bg-muted/70" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : recipes.length === 0 ? (
              <Card className="rounded-2xl border border-border bg-card p-8 text-center">
                <ChefHat className="mx-auto mb-3 h-8 w-8 text-muted-foreground/40" />
                <p className="text-callout font-semibold text-foreground">No recipes yet</p>
                <p className="mt-1 text-footnote text-muted-foreground">Check back soon for personalised recipes</p>
              </Card>
            ) : (
              <>
                {/* Context note from API */}
                {recipesData?.context_note && (
                  <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 dark:border-emerald-800/40 dark:bg-emerald-950/25">
                    <Star className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                    <p className="min-w-0 flex-1 text-caption1 leading-snug text-emerald-800 dark:text-emerald-200/90">
                      {recipesData.context_note}
                    </p>
                  </div>
                )}
                {recipes.map((recipe: any, i: number) => {
                const isOpen = expandedRecipe === i;
                const spikeVal = recipe.estimated_spike_mg_dl ?? 0;
                const spikeClassification = recipe.estimated_glucose_classification ?? "";
                const spikeBg =
                  spikeVal <= 20
                    ? "bg-emerald-100 text-emerald-700"
                    : spikeVal <= 40
                    ? "bg-amber-100 text-amber-700"
                    : "bg-red-100 text-red-600";
                const mealLabel: Record<string, string> = {
                  breakfast: "Breakfast",
                  lunch: "Lunch",
                  dinner: "Dinner",
                  snack: "Snack",
                };
                const mealAccent: Record<string, string> = {
                  breakfast: "bg-orange-50 dark:bg-orange-950/20",
                  lunch:     "bg-amber-50 dark:bg-amber-950/20",
                  dinner:    "bg-indigo-50 dark:bg-indigo-950/20",
                  snack:     "bg-rose-50 dark:bg-rose-950/20",
                };
                const mealBadge: Record<string, string> = {
                  breakfast: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
                  lunch:     "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
                  dinner:    "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
                  snack:     "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
                };
                const mealKey = recipe.meal_type?.toLowerCase() ?? "";
                const totalTime = (recipe.prep_time_min ?? 0) + (recipe.cook_time_min ?? 0);
                const macros = recipe.macros ?? {};

                return (
                  <Card
                    key={recipe.id || i}
                    className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm"
                  >
                    {/* ── Accordion Header (always visible) ── */}
                    <button
                      onClick={() => setExpandedRecipe(isOpen ? null : i)}
                      className="w-full text-left"
                    >
                      {/* Tinted top section */}
                      <div className={`px-4 pb-3 pt-3.5 ${mealAccent[mealKey] ?? ""}`}>
                        {/* Row 1: meal pill + spike badge + chevron */}
                        <div className="mb-2 flex items-center gap-2">
                          <span className={`rounded-full px-2.5 py-0.5 text-caption2 font-bold ${mealBadge[mealKey] ?? "bg-muted text-muted-foreground"}`}>
                            {mealLabel[mealKey] ?? recipe.meal_type}
                          </span>
                          <span className={`rounded-full px-2.5 py-0.5 text-caption2 font-bold ${spikeBg}`}>
                            {spikeClassification}
                          </span>
                          <ChevronDown
                            className={`ml-auto h-4 w-4 text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                          />
                        </div>
                        {/* Row 2: recipe title — primary heading inside card */}
                        <h2 className="text-headline font-bold leading-tight text-foreground">
                          {recipe.name}
                        </h2>
                      </div>

                      {/* Summary strip — aligned with insight cards */}
                      <div className="border-t border-border/40 bg-card px-4 pb-3 pt-3">
                        {/* Meta: time · kcal · BBS */}
                        <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-caption2">
                          <span>
                            <span className="text-muted-foreground">Time · </span>
                            <span className="font-semibold text-foreground">{totalTime} min</span>
                          </span>
                          <span className="hidden text-border sm:inline">·</span>
                          <span>
                            <span className="text-muted-foreground">Energy · </span>
                            <span className="font-semibold text-foreground">{macros.calories} kcal</span>
                          </span>
                          <span className="hidden text-border sm:inline">·</span>
                          <span>
                            <span className="text-muted-foreground">BBS · </span>
                            <span className="font-semibold text-blue-600 dark:text-blue-400">{recipe.estimated_bbs}</span>
                          </span>
                        </div>

                        <RecipeSectionHeading className="border-0 pb-0 font-semibold normal-case tracking-normal text-muted-foreground">
                          Macros per serving
                        </RecipeSectionHeading>
                        {/* Macros grid + spike pill */}
                        <div className="flex items-end justify-between gap-3">
                          <div className="grid grid-cols-3 gap-x-4 gap-y-2 text-caption2">
                            <div className="min-w-0">
                              <span className="block text-muted-foreground">Protein</span>
                              <span className="font-bold text-foreground">{macros.protein_g}g</span>
                            </div>
                            <div className="min-w-0">
                              <span className="block text-muted-foreground">Carbs</span>
                              <span className="font-bold text-foreground">{macros.carbs_g}g</span>
                            </div>
                            <div className="min-w-0">
                              <span className="block text-muted-foreground">Net carbs</span>
                              <span className="font-bold text-foreground">{macros.net_carbs_g}g</span>
                            </div>
                            <div className="min-w-0">
                              <span className="block text-muted-foreground">Fat</span>
                              <span className="font-bold text-foreground">{macros.fat_g}g</span>
                            </div>
                            <div className="min-w-0">
                              <span className="block text-muted-foreground">Fiber</span>
                              <span className="font-bold text-foreground">{macros.fiber_g}g</span>
                            </div>
                          </div>
                          <div className="flex shrink-0 items-center gap-1 rounded-xl bg-emerald-50 px-2.5 py-1.5 dark:bg-emerald-950/30">
                            <Zap className="h-3 w-3 text-emerald-500" />
                            <span className="text-caption2 font-black text-emerald-600 dark:text-emerald-400">+{spikeVal} mg/dL</span>
                          </div>
                        </div>
                      </div>
                    </button>

                    {/* ── Accordion Body (expanded) ── */}
                    {isOpen && (
                      <div className="border-t border-border/40 px-4 pb-4 pt-3 space-y-3">

                        {/* Why today */}
                        {recipe.why_today && (
                          <div>
                            <RecipeSectionHeading>Why this recipe</RecipeSectionHeading>
                            <p className="text-footnote leading-relaxed text-muted-foreground">
                              {recipe.why_today}
                            </p>
                          </div>
                        )}

                        {/* Ingredients */}
                        {recipe.ingredients?.length > 0 && (
                          <div>
                            <RecipeSectionHeading>Ingredients</RecipeSectionHeading>
                            <div className="flex flex-wrap gap-1.5">
                              {recipe.ingredients.map((ing: any, j: number) => (
                                <span key={j} className="rounded-full bg-muted px-2.5 py-0.5 text-caption2 font-medium text-foreground">
                                  {ing.quantity} {ing.item}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Instructions */}
                        {recipe.instructions?.length > 0 && (
                          <div>
                            <RecipeSectionHeading>How to make</RecipeSectionHeading>
                            <ol className="space-y-1.5">
                              {recipe.instructions.map((step: string, j: number) => (
                                <li key={j} className="flex items-start gap-2 text-footnote text-muted-foreground">
                                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/10 text-caption2 font-bold text-primary">{j + 1}</span>
                                  {step}
                                </li>
                              ))}
                            </ol>
                          </div>
                        )}

                        {/* BBS Boosters */}
                        {recipe.bbs_boosters?.length > 0 && (
                          <div>
                            <RecipeSectionHeading>BBS boosters</RecipeSectionHeading>
                            <div className="flex flex-wrap gap-1.5">
                              {recipe.bbs_boosters.map((b: string, j: number) => (
                                <span key={j} className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-caption2 font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                                  {b}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Substitution */}
                        {recipe.substitution && (
                          <div className="rounded-lg border border-border/40 bg-muted/30 px-3 py-2.5">
                            <RecipeSectionHeading className="mb-1.5 border-border/50 pb-1 text-compact-sm">
                              Swap idea
                            </RecipeSectionHeading>
                            <p className="text-footnote leading-relaxed text-muted-foreground">{recipe.substitution}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
              </>
            )}
          </div>
        )}

        
      </div>

      <BottomNavBar />
    </div>
  );
};

export default DailyInsightsPage;
